import { DatePipe, formatNumber } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  Validators
} from '@angular/forms';
import { AuthService, LegService } from '@approot/services';
import {
  AgGridColorPalette,
  apiURL,
  SubscriptionSink
} from '@approot/utilities';
import { LegComponent } from '@approot/widgets/strategy-detail/leg/leg.component';
import { AgChartOptions } from 'ag-charts-community';
import { catchError, distinctUntilChanged, map, switchMap, take } from 'rxjs';

interface PayoutGraphData {
  settlementDate: string;
  premium: number;
  intervals: {
    spots: number[];
    values: number[];
    breakEven: number | null;
  }[];
}

@Component({
  selector: 'app-payout-graph',
  templateUrl: './payout-graph.component.html',
  styleUrls: ['./payout-graph.component.scss']
})
export class PayoutGraphComponent implements OnInit, OnDestroy {
  @Input() isModalOpened = false;
  @Input() legComponents: LegComponent[];
  @Output() payoutGraphOpenChanged = new EventEmitter<boolean>();
  public chartOptions: AgChartOptions = {};
  public commonCross: string;
  public commonCrossProducts: any[];
  public isLoading = false;
  public minHeightPreData = 250;
  public minHeightPostData = 550;

  private subSink = new SubscriptionSink();
  private authService = inject(AuthService);
  private http = inject(HttpClient);
  private datePipe = inject(DatePipe);
  private legService = inject(LegService);
  private fb = inject(FormBuilder);
  private setupTimeoutId = undefined;

  form = this.fb.group({
    spot: [
      Number.MIN_VALUE,
      { validators: [Validators.required, this.numberControlValidator] }
    ],
    lowerBoundary: [
      Number.MIN_VALUE,
      {
        validators: [Validators.required, this.numberControlValidator]
      }
    ],
    upperBoundary: [
      Number.MIN_VALUE,
      {
        validators: [Validators.required, this.numberControlValidator]
      }
    ],
    graphCurrency: ['', { validators: [Validators.required] }],
    premiumCurrency: ['', { validators: [Validators.required] }]
  });

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.modalChanged(false);
    }
  }

  ngOnInit(): void {
    this.setup();
  }

  modalChanged(open: boolean): void {
    this.payoutGraphOpenChanged.emit(open);
  }

  submit(): void {
    if (this.commonCrossProducts.length && this.form.valid) {
      this.isLoading = true;
      this.chartOptions = {};
      const payload = {
        task: {
          taskType: 'payoutGraph',
          taskParameters: {
            minSpot: this.lowerBoundaryControl.value,
            maxSpot: this.upperBoundaryControl.value,
            currency: this.graphCurrencyControl.value,
            spotRef: this.spotControl.value,
            premiumCurrency: this.premiumCurrencyControl.value
          }
        },
        products: this.commonCrossProducts
      };

      this.authService
        .currentSession$()
        .pipe(
          switchMap(cognitoToken =>
            this.http
              .post<PayoutGraphData[]>(
                apiURL('/fx/pricing'),
                payload,
                this.getHeaders(cognitoToken.getIdToken().getJwtToken())
              )
              .pipe(
                map((payoutGraphData: PayoutGraphData[]) => {
                  this.isLoading = false;
                  this.generateOptionsChart(
                    payoutGraphData,
                    this.graphCurrencyControl.value,
                    `${this.baseCurrency}\/${this.counterCurrency} Spot`
                  );
                }),
                catchError(err => {
                  this.isLoading = false;
                  const id = this.legService.logHydraError(
                    err,
                    '/fx/pricing',
                    payload
                  );
                  return this.legService.handleError(
                    'payoutGraph error',
                    id
                  )(err);
                })
              )
          ),
          take(1)
        )
        .subscribe();
    }
  }

  numberControlValidator(control: FormControl): {
    invalidEntry: {
      value: any;
    };
  } | null {
    if (
      !isNaN(control.value) &&
      new RegExp('^\\d+\\.?\\d{0,5}$').test(control.value)
    ) {
      return null;
    }
    return { invalidEntry: { value: control.value } };
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
    this.clearSetupTimeout();
  }

  private setup(): void {
    const checkedLegs = this.checkedLegs();
    if (checkedLegs.length) {
      this.findCommonCrossProducts(checkedLegs);
      if (this.commonCross && this.commonCrossProducts.length) {
        this.setupFormControls();
      }
    }
  }

  private findCommonCrossProducts(checkedLegs: LegComponent[]): void {
    const products = checkedLegs.map(checkedLeg => {
      if (!checkedLeg.isLegDisabled) {
        const productsObject = this.legService.createProductsObject([
          {
            product: checkedLeg.form.value,
            attributes: checkedLeg.leg.attributes
          }
        ]);
        if (productsObject.length) {
          return productsObject[0];
        }
      }
    });
    this.commonCross = products.find(product => !!product && !!product.cross)
      ?.cross;

    if (this.commonCross) {
      this.commonCrossProducts = products.filter(
        product => product.cross === this.commonCross
      );
    } else {
      // retry if the user opened the panel while the leg attributes
      // were still loading
      this.clearSetupTimeout();
      this.setupTimeoutId = setTimeout(() => {
        this.setup();
      }, 1000);
    }
  }

  private clearSetupTimeout() {
    if (typeof this.setupTimeoutId === 'number') {
      clearTimeout(this.setupTimeoutId);
    }
  }

  private setupFormControls(): void {
    this.subSink.sink = this.spotControl.valueChanges
      .pipe(
        map(changes => {
          this.lowerBoundaryControl.setValue(this.lowerBoundary(changes));
          this.upperBoundaryControl.setValue(this.upperBoundary(changes));
        })
      )
      .subscribe();

    this.subSink.sink = this.lowerBoundaryControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        map(changes => {
          const lowerBoundaryValidationValue =
            changes < Number.MIN_VALUE ? Number.MIN_VALUE : changes;
          this.upperBoundaryControl.setValidators([
            Validators.required,
            this.numberControlValidator,
            Validators.min(lowerBoundaryValidationValue)
          ]);
          this.upperBoundaryControl.updateValueAndValidity();
        })
      )
      .subscribe();

    this.subSink.sink = this.upperBoundaryControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        map(changes => {
          this.lowerBoundaryControl.setValidators([
            Validators.required,
            this.numberControlValidator,
            Validators.min(Number.MIN_VALUE),
            Validators.max(changes)
          ]);
          this.lowerBoundaryControl.updateValueAndValidity();
        })
      )
      .subscribe();

    this.spotControl.setValue(this.commonCrossProducts[0].spot);
    this.graphCurrencyControl.setValue(this.baseCurrency);
    this.premiumCurrencyControl.setValue(this.counterCurrency);
    this.lowerBoundaryControl.setValue(
      this.lowerBoundary(this.spotControl.value)
    );
    this.upperBoundaryControl.setValue(
      this.upperBoundary(this.spotControl.value)
    );
  }

  private generateOptionsChart(
    payoutGraphData: PayoutGraphData[],
    xAxisName: string,
    yAxisName: string
  ): void {
    // pad data to induce discontinuity
    payoutGraphData = payoutGraphData.slice(0, 5).map(data => ({
      ...data,
      intervals: data.intervals.map(interval => ({
        ...interval,
        spots: [interval.spots[0], ...interval.spots],
        values: [null, ...interval.values]
      }))
    }));
    const series = payoutGraphData
      .map((y, index) => {
        const lineType = {
          type: 'line' as any,
          xKey: 'spot',
          yKey: 'value',
          yName: this.datePipe.transform(y.settlementDate),
          legendItemName: this.datePipe.transform(y.settlementDate),
          connectMissingData: false,
          stroke: AgGridColorPalette[index],
          marker: {
            enabled: false
          },
          tooltip: {
            renderer: params => ({
              title: null,
              content: `${formatNumber(
                params.datum.spot,
                'en-GB',
                '1.5-5'
              )}: ${formatNumber(params.datum.value, 'en-GB', '1.5-5')}`
            })
          },
          data: y.intervals
            .map(interval =>
              interval.spots.map((spot, index) => ({
                spot: spot,
                value: interval.values[index]
              }))
            )
            .flat()
        };
        const scatterType = {
          type: 'scatter' as any,
          xKey: 'spot',
          yKey: 'value',
          yName: this.datePipe.transform(y.settlementDate),
          legendItemName: this.datePipe.transform(y.settlementDate),
          showInLegend: false,
          stroke: AgGridColorPalette[index],
          fill: AgGridColorPalette[index],
          label: {
            fontWeight: 'bold',
            formatter: params => {
              return params.datum.value === 0
                ? formatNumber(params.datum.spot, 'en-GB', '1.3-3')
                : '';
            }
          },
          tooltip: {
            renderer: params => ({
              title: null,
              content: `${formatNumber(
                params.datum.spot,
                'en-GB',
                '1.5-5'
              )}: ${formatNumber(params.datum.value, 'en-GB', '1.5-5')}`
            })
          },
          data: y.intervals
            .map(interval => {
              let intervals = [];
              if (interval.breakEven) {
                intervals = [
                  ...intervals,
                  { spot: interval.breakEven, value: 0 }
                ];
              }
              return intervals;
            })
            .flat()
        };
        return [lineType, scatterType];
      })
      .flat();

    this.chartOptions = {
      background: {
        fill: 'transparent'
      },
      theme: {
        baseTheme: 'ag-default-dark',
        palette: {
          fills: AgGridColorPalette,
          strokes: AgGridColorPalette
        }
      },
      legend: {
        enabled: true,
        position: 'bottom'
      },
      axes: [
        {
          type: 'number',
          label: {
            enabled: true,
            avoidCollisions: true,
            formatter: params => formatNumber(params.value, 'en-GB', '1.0-3')
          },
          crossLines: [
            {
              type: 'line',
              value: 0,
              label: {
                text: yAxisName,
                position: 'right'
              }
            }
          ]
        },
        {
          type: 'number',
          label: {
            enabled: true,
            avoidCollisions: true,
            formatter: params => formatNumber(params.value, 'en-GB', '1.0-3')
          },
          crossLines: [
            {
              type: 'line',
              value: this.getXCrossLineValue(payoutGraphData),
              label: {
                text: xAxisName
              }
            }
          ]
        }
      ],
      series: series
    };
  }

  private lowerBoundary(spot: number): number {
    const lowerBoundary = Math.round(spot * 97) / 100;
    if (isNaN(lowerBoundary)) {
      return 0;
    }
    return lowerBoundary;
  }

  private upperBoundary(spot: number): number {
    const upperBoundary = Math.round(spot * 103) / 100;
    if (isNaN(upperBoundary)) {
      return 0;
    }
    return upperBoundary;
  }

  private getXCrossLineValue(payoutGraphData: PayoutGraphData[]): number {
    const spots = payoutGraphData
      .map(datum => datum.intervals.map(interval => interval.spots))
      .flat(2);

    const maxSpot = Math.max(...spots);
    const minSpot = Math.min(...spots);

    return minSpot + Math.abs(maxSpot - minSpot) / 2;
  }

  private checkedLegs(): LegComponent[] {
    // get all legs that have been checked
    return (
      this.legComponents?.filter(
        leg => leg.leg?.checked && leg.legHelperService.legAlive
      ) || []
    );
  }

  private getHeaders(token): {
    headers: HttpHeaders;
  } {
    let headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', token);
    return { headers };
  }

  get spotControl(): AbstractControl<number> {
    return this.form.get('spot');
  }

  get lowerBoundaryControl(): AbstractControl<number> {
    return this.form.get('lowerBoundary');
  }

  get upperBoundaryControl(): AbstractControl<number> {
    return this.form.get('upperBoundary');
  }

  get graphCurrencyControl(): AbstractControl<string> {
    return this.form.get('graphCurrency');
  }

  get premiumCurrencyControl(): AbstractControl<string> {
    return this.form.get('premiumCurrency');
  }

  get baseCurrency(): string {
    return this.commonCross.slice(0, 3);
  }

  get counterCurrency(): string {
    return this.commonCross.slice(-3);
  }
}
